import { useCallback, useEffect, useRef, useState } from "react";
import { getToken, type Me, type ServerConfig } from "../lib/api.js";
import { bytes, dateTime } from "../lib/format.js";
import { Drawer, Notice } from "../components/ui.js";
import { RemoteSession } from "rustdesk-protocol/session.js";
import {
  FileTransfer, ROOT_PATH, joinPath, parentPath, breadcrumbs,
  type RemoteEntry, type TransferProgress,
} from "rustdesk-protocol/files.js";
import { savedHash } from "../lib/credentials.js";

/**
 * Files on the remote machine.
 *
 * Runs on its own connection: RustDesk treats file transfer as a distinct
 * connection type, not a channel inside a screen session.
 */
export default function FileBrowser({
  peerId,
  me,
  serverKeyProvider,
  password,
  onClose,
}: {
  peerId: string;
  me: Me;
  serverKeyProvider: () => Promise<ServerConfig>;
  password?: string;
  onClose: () => void;
}) {
  const sessionRef = useRef<RemoteSession | null>(null);
  const ftRef = useRef<FileTransfer | null>(null);
  const uploadRef = useRef<HTMLInputElement | null>(null);

  const [status, setStatus] = useState("Connecting…");
  const [error, setError] = useState("");
  const [path, setPath] = useState(ROOT_PATH);
  const [entries, setEntries] = useState<RemoteEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [transfers, setTransfers] = useState<TransferProgress[]>([]);
  const [ready, setReady] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [editingPath, setEditingPath] = useState<string | null>(null);

  const load = useCallback(async (target: string, hidden = false) => {
    const ft = ftRef.current;
    if (!ft) return;
    setBusy(true);
    setError("");
    setSelected(null);
    try {
      // Trust the path the peer reports: it normalises what we send.
      const dir = await ft.listDir(target, hidden);
      setEntries(dir.entries);
      setPath(dir.path || target);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await serverKeyProvider();
        // File transfer is its own connection and needs its own credentials;
        // the screen session may have had none to pass on.
        const hash = password ? null : await savedHash(peerId);
        const session = new RemoteSession(
          {
            origin: window.location.origin,
            token: getToken(),
            peerId,
            serverKey: cfg.key,
            password,
            passwordHash: hash ?? undefined,
            connType: "file_transfer",
            // The peer lists this unprompted right after login. It must be
            // "/": on Windows that is the only value that yields the drive
            // list, and an empty path fails outright.
            fileTransferDir: ROOT_PATH,
            myName: me.display_name || me.name,
            myId: "web",
          },
          {
            onState: (s) => {
              if (s === "connected") setStatus("Connected");
              else if (s === "authenticating") {
                setStatus(
                  password || hash
                    ? "Signing in"
                    : "Waiting for someone at the machine to accept",
                );
              }
            },
            onPasswordRequired: (wrong) =>
              setError(
                wrong
                  ? "The saved password for this machine was not accepted. Reconnect the session and enter it again."
                  : "This machine needs a password. Reconnect the session and enter it.",
              ),
            onError: (m) => setError(m),
            onClose: (r) => { setStatus(r || "Disconnected"); setReady(false); },
            onPeerInfo: () => { if (!cancelled) { setReady(true); setStatus("Connected"); } },
            onMessage: (m) => ftRef.current?.handle(m),
          },
        );
        sessionRef.current = session;

        const ft = new FileTransfer(session, (p) => {
          setTransfers((prev) => [...prev.filter((t) => t.id !== p.id), p].slice(-6));
        });
        // Catches our own requests and the listing the peer volunteers.
        ft.onDirectory = (dir) => {
          if (cancelled) return;
          setEntries(dir.entries);
          setPath(dir.path || ROOT_PATH);
          setBusy(false);
        };
        ftRef.current = ft;
        await session.connect();
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();

    return () => {
      cancelled = true;
      sessionRef.current?.close("file browser closed");
      sessionRef.current = null;
      ftRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function enter(entry: RemoteEntry) {
    if (!entry.isDir) return;
    void load(joinPath(path, entry.name), showHidden);
  }

  async function download(entry: RemoteEntry) {
    const ft = ftRef.current;
    if (!ft) return;
    try {
      const blob = await ft.download(joinPath(path, entry.name), entry.name);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = entry.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function upload(files: FileList | null) {
    const ft = ftRef.current;
    if (!ft || !files?.length) return;
    for (const file of Array.from(files)) {
      try {
        await ft.upload(path, file);
      } catch (e) {
        setError(`${file.name}: ${(e as Error).message}`);
      }
    }
    void load(path, showHidden);
  }

  function remove(entry: RemoteEntry) {
    const ft = ftRef.current;
    if (!ft) return;
    const full = joinPath(path, entry.name);
    if (!window.confirm(`Delete ${entry.isDir ? "folder" : "file"} "${entry.name}" on the remote machine?`)) return;
    if (entry.isDir) ft.removeDir(full);
    else ft.removeFile(full);
    // The peer does not announce the result, so re-read after a moment.
    setTimeout(() => void load(path, showHidden), 600);
  }

  function rename(entry: RemoteEntry) {
    const ft = ftRef.current;
    if (!ft) return;
    const next = window.prompt(`Rename "${entry.name}" to:`, entry.name);
    if (!next || next === entry.name) return;
    ft.rename(joinPath(path, entry.name), next);
    setTimeout(() => void load(path, showHidden), 600);
  }

  function newFolder() {
    const ft = ftRef.current;
    if (!ft) return;
    const name = window.prompt("Name for the new folder:");
    if (!name) return;
    ft.createDir(joinPath(path, name));
    setTimeout(() => void load(path, showHidden), 600);
  }

  const crumbs = breadcrumbs(path);
  const atRoot = path === ROOT_PATH;

  return (
    <Drawer
      title={`Files on ${peerId}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--primary" disabled={!ready || busy || atRoot}
            onClick={() => uploadRef.current?.click()}>
            Upload here
          </button>
          <input ref={uploadRef} type="file" multiple hidden
            onChange={(e) => { void upload(e.target.files); e.target.value = ""; }} />
          <button className="btn" disabled={!ready || busy || atRoot} onClick={newFolder}>
            New folder
          </button>
          <div className="spacer" />
          <button className="btn btn--sm" disabled={!ready || busy}
            onClick={() => void load(path, showHidden)}>
            Refresh
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <Notice>{error}</Notice> : null}
        {!ready ? <p className="faint">{status}…</p> : null}

        {ready ? (
          <>
            <div className="row" style={{ gap: "0.375rem", flexWrap: "wrap" }}>
              <button className="btn btn--sm" disabled={busy || atRoot}
                onClick={() => void load(parentPath(path), showHidden)} title="Up one level">
                &uarr; Up
              </button>

              {editingPath === null ? (
                <nav className="row" style={{ gap: "0.125rem", flexWrap: "wrap", flex: 1, minWidth: 0 }}>
                  {crumbs.map((c, i) => (
                    <span key={c.path + i} className="row" style={{ gap: "0.125rem" }}>
                      {i > 0 ? <span className="faint small">/</span> : null}
                      <button
                        className="btn btn--ghost btn--sm"
                        style={{ padding: "0 0.25rem" }}
                        disabled={busy}
                        onClick={() => void load(c.path, showHidden)}
                      >
                        {c.label}
                      </button>
                    </span>
                  ))}
                  <button className="btn btn--ghost btn--sm" title="Type a path"
                    onClick={() => setEditingPath(path)}>
                    &#9998;
                  </button>
                </nav>
              ) : (
                <input
                  className="mono small"
                  autoFocus
                  value={editingPath}
                  onChange={(e) => setEditingPath(e.target.value)}
                  onBlur={() => setEditingPath(null)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { void load(editingPath, showHidden); setEditingPath(null); }
                    if (e.key === "Escape") setEditingPath(null);
                  }}
                />
              )}
            </div>

            <label className="check" style={{ margin: 0 }}>
              <input type="checkbox" checked={showHidden}
                onChange={(e) => { setShowHidden(e.target.checked); void load(path, e.target.checked); }} />
              Show hidden files
            </label>

            {transfers.length ? (
              <div className="panel"><div className="panel__body" style={{ padding: "0.625rem 0.75rem" }}>
                {transfers.map((t) => (
                  <div key={`${t.direction}-${t.id}`} style={{ marginBottom: "0.375rem" }}>
                    <div className="row small">
                      <span className="truncate" style={{ flex: 1 }}>
                        {t.direction === "upload" ? "↑" : "↓"} {t.name}
                      </span>
                      <span className="faint nowrap">
                        {t.error ? t.error : t.done ? "Done"
                          : `${bytes(t.transferred)}${t.total ? ` of ${bytes(t.total)}` : ""}`}
                      </span>
                    </div>
                    <div style={{ height: 2, background: "var(--line)", borderRadius: 2, overflow: "hidden" }}>
                      <div style={{
                        height: "100%",
                        width: `${t.total ? Math.min(100, (t.transferred / t.total) * 100) : 100}%`,
                        background: t.error ? "var(--deny)" : "var(--live)",
                      }} />
                    </div>
                  </div>
                ))}
              </div></div>
            ) : null}

            <div className="tablewrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th className="num" style={{ width: 84 }}>Size</th>
                    <th style={{ width: 140 }}>Modified</th>
                    <th style={{ width: 150 }} />
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr
                      key={e.name}
                      data-selected={selected === e.name}
                      onClick={() => setSelected(e.name)}
                      onDoubleClick={() => enter(e)}
                      style={{ cursor: e.isDir ? "pointer" : "default" }}
                    >
                      <td>
                        <span className="row" style={{ gap: "0.5rem" }}>
                          <span className="faint" aria-hidden="true">{e.isDir ? "▸" : "–"}</span>
                          {e.isDir ? (
                            <button className="btn btn--ghost btn--sm" style={{ padding: 0 }}
                              onClick={(ev) => { ev.stopPropagation(); enter(e); }}>
                              {e.name}
                            </button>
                          ) : (
                            <span className="truncate">{e.name}</span>
                          )}
                          {e.isLink ? <span className="tag">link</span> : null}
                        </span>
                      </td>
                      <td className="num faint">{e.isDir ? "" : bytes(e.size)}</td>
                      <td className="small faint">
                        {e.modified.getTime() ? dateTime(e.modified.getTime()) : "—"}
                      </td>
                      <td className="actions">
                        {!e.isDir ? (
                          <button className="btn btn--sm"
                            onClick={(ev) => { ev.stopPropagation(); void download(e); }}>
                            Download
                          </button>
                        ) : null}
                        {!atRoot ? (
                          <>
                            <button className="btn btn--sm"
                              onClick={(ev) => { ev.stopPropagation(); rename(e); }}>
                              Rename
                            </button>
                            <button className="btn btn--danger btn--sm"
                              onClick={(ev) => { ev.stopPropagation(); remove(e); }}>
                              Delete
                            </button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {!entries.length && !busy ? (
                    <tr><td colSpan={4} className="faint" style={{ padding: "1.5rem", textAlign: "center" }}>
                      This folder is empty.
                    </td></tr>
                  ) : null}
                  {busy ? (
                    <tr><td colSpan={4} className="faint" style={{ padding: "1.5rem", textAlign: "center" }}>
                      Loading…
                    </td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </Drawer>
  );
}
